import { useCallback, useEffect, useState } from "react";
import { Loader2, Mail, Send, Trash2, UserPlus, Users } from "lucide-react";
import { z } from "zod";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/authContext";

type TeamRole = "viewer" | "editor" | "admin";
type TeamStatus = "pending" | "active" | "revoked";

interface TeamMemberRow {
  id: string;
  invited_email: string | null;
  name: string;
  role: string;
  status: string;
  created_at: string;
  accepted_at: string | null;
}

interface TeamDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const inviteSchema = z.object({
  email: z.string().email("Enter a valid email"),
  role: z.enum(["viewer", "editor", "admin"]),
});

const roleStyles: Record<string, string> = {
  admin: "bg-primary/10 text-primary border-primary/20",
  editor: "bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/20",
  viewer: "bg-muted text-muted-foreground border-border",
};

function initialsFromEmail(email: string | null, name?: string) {
  if (name && name.trim()) {
    const parts = name.trim().split(/\s+/);
    return (parts[0][0] + (parts[1]?.[0] ?? "")).toUpperCase();
  }
  if (!email) return "??";
  const local = email.split("@")[0] ?? "";
  return (local.slice(0, 2) || "??").toUpperCase();
}

export default function TeamDrawer({ open, onOpenChange }: TeamDrawerProps) {
  const { user } = useAuth();
  const [members, setMembers] = useState<TeamMemberRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<TeamRole>("viewer");
  const [submitting, setSubmitting] = useState(false);

  const loadMembers = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    const { data, error } = await supabase
      .from("team_members")
      .select("id, invited_email, name, role, status, created_at, accepted_at")
      .eq("invited_by_user_id", user.id)
      .order("created_at", { ascending: false });
    setLoading(false);
    if (error) {
      toast.error("Failed to load team members");
      return;
    }
    setMembers((data ?? []) as TeamMemberRow[]);
  }, [user]);

  useEffect(() => {
    if (open) loadMembers();
  }, [open, loadMembers]);

  const handleInvite = async () => {
    if (!user) return;
    const parsed = inviteSchema.safeParse({ email: email.trim().toLowerCase(), role });
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message ?? "Invalid input");
      return;
    }
    const cleanEmail = parsed.data.email;
    if (members.some(m => m.invited_email?.toLowerCase() === cleanEmail && m.status !== "revoked")) {
      toast.error("That email is already invited");
      return;
    }
    setSubmitting(true);
    const { data: inserted, error } = await supabase
      .from("team_members")
      .insert({
        user_id: user.id,
        invited_by_user_id: user.id,
        invited_email: cleanEmail,
        name: cleanEmail.split("@")[0] ?? cleanEmail,
        role: parsed.data.role,
        status: "pending",
      })
      .select("id, invited_email, name, role, status, created_at, accepted_at")
      .single();
    if (error || !inserted) {
      setSubmitting(false);
      toast.error("Failed to send invitation");
      return;
    }
    setMembers(prev => [inserted as TeamMemberRow, ...prev]);
    setEmail("");
    setRole("viewer");

    // Fire the invitation email via the existing transactional sender.
    const inviterName = (user.user_metadata?.full_name as string | undefined) || undefined;
    const { error: emailError } = await supabase.functions.invoke("send-transactional-email", {
      body: {
        templateName: "team-invitation",
        recipientEmail: cleanEmail,
        idempotencyKey: `team-invitation-${inserted.id}`,
        templateData: {
          inviterName,
          inviterEmail: user.email,
          role: parsed.data.role,
          acceptUrl: `${window.location.origin}/auth`,
        },
      },
    });
    setSubmitting(false);
    if (emailError) {
      toast.warning("Invite saved, but email could not be sent");
    } else {
      toast.success(`Invitation sent to ${cleanEmail}`);
    }
  };

  const handleRemove = async (id: string) => {
    const prev = members;
    setMembers(p => p.filter(m => m.id !== id));
    const { error } = await supabase.from("team_members").delete().eq("id", id);
    if (error) {
      setMembers(prev);
      toast.error("Failed to remove team member");
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader className="text-left">
          <SheetTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" /> Your Team
          </SheetTitle>
          <SheetDescription>
            Invite teammates to collaborate on your workspace.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-3 rounded-lg border border-border p-4 bg-card">
          <div className="space-y-2">
            <Label htmlFor="invite-email" className="text-xs font-medium">Invite member</Label>
            <Input
              id="invite-email"
              type="email"
              placeholder="teammate@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="off"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-medium">Role</Label>
            <Select value={role} onValueChange={(v) => setRole(v as TeamRole)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="viewer">Viewer — read-only access</SelectItem>
                <SelectItem value="editor">Editor — can edit content</SelectItem>
                <SelectItem value="admin">Admin — full access</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button
            onClick={handleInvite}
            disabled={submitting || !email.trim()}
            className="w-full gap-2"
            size="sm"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Send Invite
          </Button>
        </div>

        <div className="mt-8">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-foreground">Members</h3>
            <span className="text-xs text-muted-foreground">{members.length} {members.length === 1 ? "member" : "members"}</span>
          </div>

          {loading ? (
            <div className="flex justify-center py-8 text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          ) : members.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border p-6 text-center">
              <UserPlus className="w-6 h-6 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No members yet. Invite your first teammate above.</p>
            </div>
          ) : (
            <ul className="space-y-2">
              {members.map((m) => {
                const displayEmail = m.invited_email ?? "";
                const displayName = m.name && m.name !== displayEmail.split("@")[0] ? m.name : displayEmail;
                return (
                  <li
                    key={m.id}
                    className="flex items-center gap-3 rounded-lg border border-border bg-card p-3"
                  >
                    <Avatar className="w-9 h-9">
                      <AvatarFallback className="text-xs bg-primary/10 text-primary">
                        {initialsFromEmail(displayEmail, m.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{displayName || displayEmail}</p>
                      <p className="text-xs text-muted-foreground truncate flex items-center gap-1">
                        <Mail className="w-3 h-3" /> {displayEmail || "—"}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <Badge variant="outline" className={`text-[10px] capitalize ${roleStyles[m.role] ?? roleStyles.viewer}`}>
                        {m.role}
                      </Badge>
                      <span className="text-[10px] text-muted-foreground capitalize">{m.status}</span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemove(m.id)}
                      className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                      aria-label="Remove member"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
